/*
 * The Feed Medic severity ladder: one module owning every mapping from a
 * status value to how it looks. Kept separate from copy.ts because the report
 * is rendered on more than one surface (the in-app panel, the share page, and
 * — still on its own `.fm-*` styles — code/admin/feedMedic.php), so centralising
 * the visual language here is what makes consolidating those later a move
 * rather than a rewrite.
 *
 * Colour choice, and why it is mixed:
 *
 * - ok uses the `--success` token pair added to tw.css for this report, applied
 *   the same way Badge and Alert apply `--destructive`: a /10 tint with the
 *   solid colour as text.
 * - critical uses `--destructive` directly, so it matches every other
 *   destructive surface in the app.
 * - warning uses Tailwind's amber utilities rather than the `--warning` token.
 *   That token is amber-500 and is already consumed as an icon fill by
 *   custom-toast, so it is too light to read as text and cannot be darkened
 *   without changing toasts. The amber-50/200/800 trio here is exactly what
 *   account/EmailCard.tsx uses for its warning alert, so this follows house
 *   precedent rather than departing from it.
 */

import {
    CircleCheck,
    CircleMinus,
    CircleX,
    Clock,
    Info,
    LoaderCircle,
    TriangleAlert,
} from 'lucide-react';

import type {BucketDisplayState, BucketStatus, Finding, FindingSeverity, Verdict} from './types';

interface Tone {
    /**
     * Colour for the row's glyph. Finding rows share one neutral background —
     * the section header carries the severity colour now — so the glyph is the
     * only per-row signal and has to stay legible on that neutral.
     */
    text: string;
    /**
     * Badge/chip treatment, for the per-item status chips in the content detail
     * panel. Lives here rather than in that panel so severity colour is defined
     * once: the chips previously carried their own copy of these classes.
     */
    chip: string;
    icon: typeof CircleCheck;
}

/*
 * Note `skipped` is a real finding severity, not just a bucket state:
 * ContentQualityCheck::tagFinding emits it when zero item pages were readable,
 * specifically so "No pages provide og:title" isn't shown as a real finding
 * when it's only an artifact of having no data. Its comment asks renderers for
 * a grey treatment distinct from info-blue — honoured here.
 */
export const FINDING_TONE: Record<FindingSeverity, Tone> = {
    ok: {
        text: 'tw:text-success',
        chip: 'tw:border-success/25 tw:bg-success/10 tw:text-success',
        icon: CircleCheck,
    },
    info: {
        text: 'tw:text-primary',
        chip: 'tw:border-border tw:bg-secondary tw:text-secondary-foreground',
        icon: Info,
    },
    warning: {
        /* amber-700, not the lighter amber-500: a bare glyph has no filled disc
         * to sit against, and amber-500 on the neutral row measures 2.08:1. */
        text: 'tw:text-amber-700',
        chip: 'tw:border-amber-200 tw:bg-amber-50 tw:text-amber-800 tw:dark:bg-amber-950/40 tw:dark:text-amber-300',
        icon: TriangleAlert,
    },
    critical: {
        text: 'tw:text-destructive',
        chip: 'tw:border-destructive/30 tw:bg-destructive/10 tw:text-destructive',
        icon: CircleX,
    },
    skipped: {
        text: 'tw:text-muted-foreground',
        chip: 'tw:border-border tw:bg-muted tw:text-muted-foreground',
        icon: CircleMinus,
    },
};

interface StateTone {
    /**
     * Tinted header band: a pale background plus a title colour that reads on it.
     *
     * Measured against the 50-weight backgrounds these pair with: the 800-weight
     * titles land at 6.81:1 (green), 6.84:1 (amber) and 7.60:1 (red), comfortably
     * clear of AA. Note the severity colour cannot be used as its own title colour —
     * `--success` and `--destructive` on a tint of themselves measure 4.09:1 and
     * 3.64:1, which is why these are palette weights rather than the tokens.
     */
    band: string;
    /**
     * Icon colour, one step lighter than the title so the glyph reads as a mark
     * rather than competing with the words. At 600-weight on a 50-weight ground
     * these measure 3.15:1, 3.07:1 and 4.41:1 — above the 3:1 WCAG 1.4.11 asks of
     * meaningful non-text, though green and amber only just.
     */
    iconTone: string;
    /**
     * Badge classes. Only the compact pending/skipped/running rows render a badge
     * now — the card states put their count in the header band instead — so this
     * is absent for those three rather than left as config that renders nowhere.
     */
    badge?: string;
    icon: typeof CircleCheck;
    /** Whether the section's detail should start expanded. */
    expanded: boolean;
}

export const BUCKET_TONE: Record<BucketDisplayState, StateTone> = {
    ok: {
        band:     'tw:bg-green-50 tw:text-green-800 tw:dark:bg-green-950/40 tw:dark:text-green-200',
        iconTone: 'tw:text-green-600 tw:dark:text-green-400',
        icon:     CircleCheck,
        expanded: false,
    },
    warning: {
        band:     'tw:bg-amber-50 tw:text-amber-800 tw:dark:bg-amber-950/40 tw:dark:text-amber-200',
        iconTone: 'tw:text-amber-600 tw:dark:text-amber-400',
        icon:     TriangleAlert,
        expanded: true,
    },
    critical: {
        band:     'tw:bg-red-50 tw:text-red-800 tw:dark:bg-red-950/40 tw:dark:text-red-200',
        iconTone: 'tw:text-red-600 tw:dark:text-red-400',
        icon:     CircleX,
        expanded: true,
    },
    skipped: {
        band:     'tw:bg-muted tw:text-muted-foreground',
        iconTone: 'tw:text-muted-foreground',
        badge:    'tw:bg-muted tw:text-muted-foreground tw:border-border',
        icon:     CircleMinus,
        expanded: false,
    },
    pending: {
        band:     'tw:bg-muted tw:text-muted-foreground',
        iconTone: 'tw:text-muted-foreground',
        badge:    'tw:bg-muted tw:text-muted-foreground tw:border-border',
        icon:     Clock,
        expanded: false,
    },
    running: {
        band:     'tw:bg-secondary tw:text-secondary-foreground',
        iconTone: 'tw:text-primary',
        badge:    'tw:bg-secondary tw:text-secondary-foreground tw:border-border',
        icon:     LoaderCircle,
        expanded: false,
    },
};

/** `pending`/`queued` are run states rather than verdicts — see copy.verdicts. */
export const VERDICT_TONE: Record<Verdict | 'pending' | 'queued', string> = {
    healthy:  'tw:bg-success/10 tw:text-success tw:border-success/25',
    warnings: 'tw:bg-amber-50 tw:text-amber-800 tw:border-amber-200 tw:dark:bg-amber-950/40 tw:dark:text-amber-300',
    critical: 'tw:bg-destructive/10 tw:text-destructive tw:border-destructive/30',
    error:    'tw:bg-destructive/10 tw:text-destructive tw:border-destructive/30',
    pending:  'tw:bg-secondary tw:text-secondary-foreground tw:border-border',
    /* Muted rather than secondary: nothing is happening yet, so it should read
     * quieter than a run that is actually under way. */
    queued:   'tw:bg-muted tw:text-muted-foreground tw:border-border',
};

/** Findings whose severity should never escalate a section's headline state. */
const NON_ESCALATING: ReadonlySet<FindingSeverity> = new Set<FindingSeverity>(['ok', 'info', 'skipped']);

/**
 * Findings that narrow how every other row in their section should be read, and
 * so must stay visible even though they need no action.
 *
 * 'pages-proxy-sampled' is emitted by ContentQualityCheck when only part of the
 * sample could be read, and the comment at its emission site states the key "is
 * part of the contract" with the renderer. Rolled up behind a "13 checks passed"
 * twist-out, every row below it silently claims broader coverage than it has.
 */
const SCOPE_NOTE_KEYS: ReadonlySet<string> = new Set(['pages-proxy-sampled']);

/**
 * Derive what a section header shows from its stored status plus its findings.
 *
 * `runTerminal` disambiguates the one status that means two different things:
 * `incomplete` is "not its turn yet" during a live run, but "short-circuited by
 * an earlier fatal check" once the run has closed. The caller determines this
 * from the presence of `result.verdict`, which is only set on terminal close.
 *
 * A bucket whose own status is `error` presents as `critical` rather than
 * getting its own state — the findings it captured before failing are usually
 * the most useful part of the report, so it should read as a problem to look
 * at, not as a broken panel.
 */
export function bucketDisplayState(
    bucket: {status?: BucketStatus; findings?: Finding[]} | undefined,
    runTerminal: boolean,
): BucketDisplayState {
    if (!bucket) return runTerminal ? 'skipped' : 'pending';

    switch (bucket.status) {
        case 'incomplete': return runTerminal ? 'skipped' : 'pending';
        case 'running':    return 'running';
        case 'skipped':    return 'skipped';
        case 'error':      return 'critical';
        default:           break;
    }

    const findings = bucket.findings ?? [];
    if (findings.some((f) => f.severity === 'critical')) return 'critical';
    if (findings.some((f) => f.severity === 'warning'))  return 'warning';

    return 'ok';
}

/**
 * Split findings into the ones a reader needs to act on, the few that qualify
 * the section's scope, and the rest, which are only evidence a check ran. Lets a
 * section lead with problems and roll the remainder up behind a count.
 *
 * `scopeNotes` is deliberately keyed off SCOPE_NOTE_KEYS rather than off the
 * `info` severity: most info findings are ordinary commentary and belong in the
 * roll-up, so promoting all of them would put rows back in front of the reader
 * that they cannot act on.
 */
export function partitionFindings(findings: Finding[] = []): {
    problems: Finding[];
    scopeNotes: Finding[];
    passing: Finding[];
} {
    const problems: Finding[]   = [];
    const scopeNotes: Finding[] = [];
    const passing: Finding[]    = [];

    for (const finding of findings) {
        if (SCOPE_NOTE_KEYS.has(finding.key))            scopeNotes.push(finding);
        else if (NON_ESCALATING.has(finding.severity))    passing.push(finding);
        else                                              problems.push(finding);
    }

    return {problems, scopeNotes, passing};
}

/** SettingsReviewCheck emits `message` where every other check emits `headline`. */
export function findingHeadline(finding: Finding): string {
    return finding.headline ?? finding.message ?? '';
}

export interface ResultCounts {
    issues: number;
    warnings: number;
    passed: number;
    skipped: number;
}

/**
 * Tally every finding across all four sections, for the run-level header.
 *
 * `info` findings count as passed: they carry context rather than something to
 * act on, and listing them separately would put a third number in front of the
 * reader without telling them anything they can do about it.
 */
export function countFindings(result: {
    accessibility?: {findings?: Finding[]};
    content?: {findings?: Finding[]};
    social?: {findings?: Finding[]};
    settings?: {findings?: Finding[]};
}): ResultCounts {
    const counts: ResultCounts = {issues: 0, warnings: 0, passed: 0, skipped: 0};

    for (const bucket of [result.accessibility, result.content, result.social, result.settings]) {
        for (const finding of bucket?.findings ?? []) {
            switch (finding.severity) {
                case 'critical': counts.issues += 1;   break;
                case 'warning':  counts.warnings += 1; break;
                case 'skipped':  counts.skipped += 1;  break;
                default:         counts.passed += 1;   break;
            }
        }
    }

    return counts;
}
